/* ============================================================
   storage — the share dialog, and the public page behind a link.

   Two components, one idea, kept together because they are two
   halves of the same promise: this dialog says what the link
   will do, and that page is what it does.

   The dialog is deliberately talkative. Everything else in this
   app is private and permission-checked; a share is the one
   action that takes something out of that and puts it on the
   open internet, and a button that quietly did it would be the
   wrong shape for the consequence.

   A link is one of two things:
     View only   the visitor watches, looks and reads in the page;
                 there is no download (see "View-only public links"
                 in server/nas.py for what that does and does not
                 promise)
     Download    the visitor gets the file, as links always did
   and it can expire.
   ============================================================ */

import React, { useEffect, useMemo, useState } from 'react';
import { Icon, fileIconName, fmtBytes, fmtDate } from './lib';
import { api } from './api';
import MediaPlayer from './MediaPlayer';
import { useStreams, qualitiesFrom, streamNote } from './streams';

const EXPIRIES = [
  { v: null, label: 'Never' },
  { v: 86400, label: '1 day' },
  { v: 7 * 86400, label: '7 days' },
  { v: 30 * 86400, label: '30 days' },
];

const untilText = (ts) => (ts ? `until ${fmtDate(ts)}` : 'with no end date');

/* ── the dialog ──────────────────────────────────────────────────────── */

function ModePicker({ mode, setMode, kind, disabled }) {
  const it = kind === 'folder' ? 'this folder' : 'this file';
  return (
    <div className="st-share-modes" role="radiogroup" aria-label="What the link allows">
      <label className={`st-share-mode${mode === 'view' ? ' is-on' : ''}`}>
        <input type="radio" name="st-share-mode" checked={mode === 'view'} disabled={disabled}
               onChange={() => setMode('view')}/>
        <span>
          <b>View only</b>
          <span className="meta">Anyone with the link can watch and look at {it} in their browser. No download button.</span>
        </span>
      </label>
      <label className={`st-share-mode${mode === 'download' ? ' is-on' : ''}`}>
        <input type="radio" name="st-share-mode" checked={mode === 'download'} disabled={disabled}
               onChange={() => setMode('download')}/>
        <span>
          <b>View and download</b>
          <span className="meta">Anyone with the link can download {it}.</span>
        </span>
      </label>
    </div>
  );
}

export function ShareDialog({ token, row, kind, onClose, onChanged, toast }) {
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState('view');
  const [expiresIn, setExpiresIn] = useState(null);
  const name = kind === 'file' ? row.raw?.original_name || row.label : row.label;

  // An existing link: ask for its terms (sharing is idempotent — asking with
  // no new terms returns the link as it stands).
  useEffect(() => {
    if (!row.share) return;
    api.share(token, kind, row.id).then((r) => { setShare(r); setMode(r.mode || 'download'); }).catch(() => {
      setShare({ token: row.share });
    });
  }, [token, kind, row.id, row.share]);

  const url = share ? `${window.location.origin}/s/${share.token}` : '';

  const publish = async () => {
    setBusy(true);
    try {
      const r = await api.share(token, kind, row.id, { mode, expires_in: expiresIn });
      setShare(r);
      onChanged?.();
    } catch (e) {
      toast?.('err', 'Could not share this', e.message);
    } finally { setBusy(false); }
  };

  const retune = async (opts) => {
    setBusy(true);
    try {
      const r = await api.share(token, kind, row.id, opts);
      setShare(r);
      if (r.mode) setMode(r.mode);
      toast?.('ok', 'Link updated', 'Same link — what it allows has changed.');
    } catch (e) {
      toast?.('err', 'Could not change the link', e.message);
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.unshare(token, kind, row.id);
      setShare(null);
      onChanged?.();
      toast?.('ok', 'Link turned off', `${name} is private again.`);
    } catch (e) {
      toast?.('err', 'Could not stop sharing', e.message);
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused in plenty of ordinary situations (an
      // insecure origin, a browser that wants a gesture it did not see).
      // Selecting the text is a worse experience but always available.
      const el = document.getElementById('st-share-url');
      el?.select?.();
      toast?.('info', 'Copy it from the box', 'Your browser would not let the page write to the clipboard.');
    }
  };

  return (
    <div className="modal-backdrop st-backdrop" role="presentation"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal st-modal st-share-modal" role="dialog" aria-modal="true"
           aria-label={`Share ${name}`}>
        <header className="st-props-head">
          <Icon name={share ? 'share' : (kind === 'folder' ? 'folder' : fileIconName(row.raw?.mime_type, name))}
                size={18} className={kind === 'folder' ? 'st-icon-folder' : 'st-icon-file'}/>
          <h2 className="h3 st-props-title" title={name}>{name}</h2>
          <button type="button" className="iconbtn" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={14}/>
          </button>
        </header>

        {!share ? (
          <>
            <p className="st-share-lead">
              Everything here needs an account. A public link is the exception:
              <strong> anyone who has it can open {kind === 'folder' ? 'this folder' : 'this file'},
              without signing in.</strong>
            </p>
            <ModePicker mode={mode} setMode={setMode} kind={kind} disabled={busy}/>
            <label className="st-share-exp">
              <span className="meta">Link works</span>
              <select className="input" value={expiresIn ?? ''} disabled={busy}
                      onChange={(e) => setExpiresIn(e.target.value ? Number(e.target.value) : null)}>
                {EXPIRIES.map((x) => <option key={x.label} value={x.v ?? ''}>{x.v ? `for ${x.label}` : 'until you turn it off'}</option>)}
              </select>
            </label>
            {kind === 'folder' && (
              <p className="meta st-share-note">
                A shared folder shows whatever is in it when someone opens the link — including
                files added later. Secure folders inside it stay shut.
              </p>
            )}
            {mode === 'view' && (
              <p className="meta st-share-note">
                View only shows photos, video, audio, PDFs and text in the page; other kinds of
                file are listed but cannot be opened. It removes the download button — it cannot
                stop someone recording their screen.
              </p>
            )}
            <div className="st-share-actions">
              <button type="button" className="btn btn--sm" onClick={publish} disabled={busy}>
                <Icon name="share" size={14}/> {busy ? 'Making a link…' : 'Create a public link'}
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <p className="st-share-lead">
              <Icon name="share" size={14}/> This {kind} is public — anyone with the link can
              {share.mode === 'view' ? ' view it (no download)' : ' download it'}, {untilText(share.expires_at)}.
            </p>
            <div className="st-share-url">
              <input id="st-share-url" className="input" readOnly value={url}
                     onFocus={(e) => e.target.select()} aria-label="Public link"/>
              <button type="button" className="btn btn--sm" onClick={copy}>
                <Icon name={copied ? 'check' : 'copy'} size={14}/> {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {share.mode && (
              <>
                <ModePicker mode={mode} kind={kind} disabled={busy}
                            setMode={(m) => { setMode(m); retune({ mode: m }); }}/>
                <label className="st-share-exp">
                  <span className="meta">Change expiry</span>
                  <select className="input" value="" disabled={busy}
                          onChange={(e) => { if (e.target.value !== '') retune({ expires_in: e.target.value === 'never' ? null : Number(e.target.value) }); }}>
                    <option value="">{share.expires_at ? `Ends ${fmtDate(share.expires_at)}` : 'Never ends'}</option>
                    <option value="never">Never end</option>
                    {EXPIRIES.filter((x) => x.v).map((x) => <option key={x.label} value={x.v}>{`End ${x.label} from now`}</option>)}
                  </select>
                </label>
              </>
            )}
            {Number.isFinite(share.hits) && (
              <p className="meta st-share-note">
                Opened {share.hits} time{share.hits === 1 ? '' : 's'}
                {share.created_at ? ` · shared ${fmtDate(share.created_at)}` : ''}
              </p>
            )}
            <div className="st-share-actions">
              <a className="btn btn--ghost btn--sm" href={url} target="_blank" rel="noreferrer">
                <Icon name="external" size={14}/> Open it
              </a>
              <button type="button" className="btn btn--danger btn--sm" onClick={revoke} disabled={busy}>
                Stop sharing
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── the public page ─────────────────────────────────────────────────── */

/* What a view link can show a file as, from what the server offered. */
function viewKind(f) {
  const m = (f.mime_type || '').toLowerCase();
  if (!f.view && !f.streams) return 'none';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (m.startsWith('image/')) return 'image';
  if (m === 'application/pdf') return 'pdf';
  return 'text';
}

function PublicText({ url }) {
  const [text, setText] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(url, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((t) => setText(t.length > 400000 ? `${t.slice(0, 400000)}\n\n… (the rest is not shown)` : t))
      .catch((e) => { if (e.name !== 'AbortError') setErr(e.message); });
    return () => ctrl.abort();
  }, [url]);
  if (err) return <p className="meta">Could not load this file.</p>;
  if (text === null) return <span className="skeleton" style={{ height: 120, display: 'block' }}/>;
  return <pre className="st-public-text">{text}</pre>;
}

/** One file, shown in the page, on a view-only link. */
function PublicViewer({ token, file }) {
  const kind = viewKind(file);
  const viewUrl = file.view ? api.publicDownloadUrl(file.view) : null;
  const streams = useStreams(kind === 'video' && file.streams ? () => api.publicJson(file.streams) : null,
    kind === 'video' ? file.id || file.name : null);
  const qualities = useMemo(() => (streams
    ? qualitiesFrom(streams, (q) => api.publicDownloadUrl(`/api/nas/public/${token}/s/${file.id}/${q}`), viewUrl)
    : undefined), [streams, token, file.id, viewUrl]);

  if (kind === 'image') {
    return <img className="st-public-img" src={viewUrl} alt={file.name}/>;
  }
  if (kind === 'video' || kind === 'audio') {
    if (kind === 'video' && !streams) return <span className="skeleton st-public-skel"/>;
    return (
      <div className="st-public-player">
        <MediaPlayer key={file.id || file.name} src={viewUrl} name={file.name} kind={kind}
                     mediaKey={`public:${file.id || file.name}`}
                     qualities={qualities} qualityNote={streamNote(streams)}/>
      </div>
    );
  }
  if (kind === 'pdf') {
    return <iframe className="st-public-pdf" src={viewUrl} title={file.name}/>;
  }
  if (kind === 'text') return <PublicText url={viewUrl}/>;
  return <p className="meta">This kind of file cannot be shown here.</p>;
}

/**
 * What someone without an account sees. No nav, no login prompt, no hint that
 * there is a larger system behind it — they were given one thing.
 */
export function PublicShare({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);     // the file being viewed, in a folder

  useEffect(() => {
    const ctrl = new AbortController();
    api.publicMeta(token, ctrl.signal)
      .then(setData)
      .catch((e) => { if (e.name !== 'AbortError') setError(e.message || 'This link is not valid'); });
    return () => ctrl.abort();
  }, [token]);

  useEffect(() => {
    // A shared page should not tell the next site where it came from.
    const m = document.createElement('meta');
    m.name = 'referrer'; m.content = 'no-referrer';
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  if (error) {
    return (
      <div className="st-public">
        <div className="st-public-card">
          <Icon name="warn" size={30}/>
          <h1 className="h2">This link does not work</h1>
          <p className="meta">{error}</p>
          <p className="meta">It may have been turned off or expired, or the file may have been deleted.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="st-public">
        <div className="st-public-card">
          <span className="skeleton" style={{ height: 28, display: 'block', width: '60%' }}/>
          <span className="skeleton" style={{ height: 44, display: 'block' }}/>
        </div>
      </div>
    );
  }

  const viewing = data.mode === 'view';

  if (data.kind === 'file') {
    const kind = viewing ? viewKind(data) : 'none';
    return (
      <div className={`st-public${kind !== 'none' ? ' st-public--wide st-public--view' : ''}`}>
        <div className="st-public-card">
          {kind === 'none' && <Icon name={fileIconName(data.mime_type, data.name)} size={34} className="st-icon-file"/>}
          <h1 className="h2 st-public-name" title={data.name}>{data.name}</h1>
          <p className="meta">{fmtBytes(data.size)} · shared from storage.ojee.net</p>
          {viewing ? (
            <PublicViewer token={token} file={data}/>
          ) : (
            <a className="btn st-public-btn" href={api.publicDownloadUrl(data.download)}>
              <Icon name="download" size={15}/> Download
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="st-public st-public--wide">
      <div className="st-public-card">
        <Icon name="folder" size={30} className="st-icon-folder"/>
        <h1 className="h2 st-public-name" title={data.name}>{data.name}</h1>
        <p className="meta">
          {data.files.length} file{data.files.length === 1 ? '' : 's'} · {fmtBytes(data.bytes)}
          {' · shared from storage.ojee.net'}{viewing ? ' · view only' : ''}
        </p>

        {viewing && open && (
          <div className="st-public-open">
            <div className="st-public-open-head">
              <b title={open.name}>{open.name}</b>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(null)}>
                <Icon name="close" size={13}/> Close
              </button>
            </div>
            <PublicViewer token={token} file={open}/>
          </div>
        )}

        {data.files.length === 0 ? (
          <p className="meta st-public-empty">There is nothing in this folder.</p>
        ) : (
          <div className="st-public-list">
            {data.files.map((f) => {
              const inner = (
                <>
                  <Icon name={fileIconName(f.mime_type, f.name)} size={15} className="st-icon-file"/>
                  <span className="st-name-text" title={f.name}>{f.name}</span>
                  <span className="meta st-public-where" title={f.where}>{f.where || ''}</span>
                  <span className="tnum st-public-size">{fmtBytes(f.size)}</span>
                </>
              );
              if (!viewing) {
                return (
                  <a key={f.id} className="st-row st-public-row" href={api.publicDownloadUrl(f.download)}>
                    {inner}<Icon name="download" size={14}/>
                  </a>
                );
              }
              const can = viewKind(f) !== 'none';
              return (
                <button key={f.id} type="button" disabled={!can}
                        className={`st-row st-public-row${open?.id === f.id ? ' is-on' : ''}`}
                        title={can ? 'View' : 'This kind of file cannot be shown here'}
                        onClick={() => { setOpen(f); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  {inner}<Icon name={can ? 'eye' : 'lock'} size={14}/>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
